import type { GridRowId } from "@mui/x-data-grid-premium";
import { PAGE_SIZE } from "./filterGridHelper.tsx";

export interface LimitedRangeSelection {
  fetchRange?: {
    start: number;
    end: number;
  };
  selectionStart: number;
  selectionEnd: number;
  scrollIndex: number;
}

export const getLimitedRangeSelection = (
  rowIds: GridRowId[],
  requestedSelection: Iterable<GridRowId>,
  currentSelection: Iterable<GridRowId>,
  skeletonRowIds: Iterable<GridRowId>,
  anchorId: GridRowId,
  limit: number
): LimitedRangeSelection | undefined => {
  const anchorIndex = rowIds.indexOf(anchorId);
  if (anchorIndex < 0) {
    return undefined;
  }

  const requestedIds = new Set(requestedSelection);
  const selectedIds = new Set(currentSelection);
  const skeletonIds = new Set(skeletonRowIds);
  const addedRowIndexes = rowIds
    .map((rowId, index) => (requestedIds.has(rowId) && !selectedIds.has(rowId) ? index : undefined))
    .filter((index): index is number => index !== undefined);
  const isForward = addedRowIndexes.every((index) => index > anchorIndex);
  const isBackward = addedRowIndexes.every((index) => index < anchorIndex);
  if (addedRowIndexes.length === 0 || isForward === isBackward) {
    return undefined;
  }

  const direction = isForward ? 1 : -1;
  let remainingCapacity = limit - selectedIds.size;
  if (remainingCapacity <= 0) {
    return undefined;
  }

  let firstSkeletonIndex: number | undefined;
  let boundaryIndex = anchorIndex;
  for (let index = anchorIndex + direction; index >= 0 && index < rowIds.length; index += direction) {
    const rowId = rowIds[index];
    if (rowId === undefined || !requestedIds.has(rowId)) {
      break;
    }
    if (skeletonIds.has(rowId) && firstSkeletonIndex === undefined) {
      firstSkeletonIndex = index;
    }
    if (!selectedIds.has(rowId)) {
      remainingCapacity -= 1;
    }
    boundaryIndex = index;
    if (remainingCapacity === 0) {
      break;
    }
  }

  const fetchStart =
    firstSkeletonIndex === undefined ? undefined : Math.floor(firstSkeletonIndex / PAGE_SIZE) * PAGE_SIZE;
  const fetchEnd = fetchStart === undefined ? undefined : Math.min(rowIds.length - 1, fetchStart + PAGE_SIZE - 1);
  const scrollIndex =
    fetchStart === undefined || fetchEnd === undefined
      ? boundaryIndex
      : isForward
        ? Math.min(boundaryIndex, fetchEnd)
        : Math.max(boundaryIndex, fetchStart);

  return {
    fetchRange: fetchStart === undefined || fetchEnd === undefined ? undefined : { start: fetchStart, end: fetchEnd },
    selectionStart: Math.min(anchorIndex, scrollIndex),
    selectionEnd: Math.max(anchorIndex, scrollIndex),
    scrollIndex,
  };
};
